import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ChevronLeft, Save, Play, Pause, Loader2, Settings, Eye, BarChart3, ShieldAlert, LayoutTemplate, Activity } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { JourneyCanvas, TemplateGallery, AnalyticsPanel } from '@/components/journey-builder';
import JourneyExecutionsPanel from '@/components/journey-builder/JourneyExecutionsPanel';
import ExitConditionsConfig from '@/components/journey-builder/ExitConditionsConfig';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Convert legacy journey format to React Flow format
const legacyToFlowFormat = (journey) => {
  if (!journey) return { nodes: [], edges: [] };
  
  // If already in flow format
  if (journey.flow_data?.nodes) {
    // Ensure exit_triggers are synced to trigger node
    const flowData = { ...journey.flow_data };
    const exitTriggers = journey.exit_conditions || journey.exit_triggers || [];
    
    // Find and update trigger node with exit_triggers
    if (exitTriggers.length > 0 && flowData.nodes) {
      flowData.nodes = flowData.nodes.map(node => {
        if (node.type === 'trigger') {
          return {
            ...node,
            data: {
              ...node.data,
              exit_triggers: exitTriggers,
            },
          };
        }
        return node;
      });
    }
    
    return flowData;
  }
  
  // Convert legacy format
  const nodes = [];
  const edges = [];
  let yOffset = 0;
  const xCenter = 300;
  const ySpacing = 150;
  
  // Get exit conditions
  const exitTriggers = journey.exit_conditions || journey.exit_triggers || [];
  
  // Add trigger node with exit_triggers
  const triggerId = `trigger_${Date.now()}`;
  nodes.push({
    id: triggerId,
    type: 'trigger',
    position: { x: xCenter, y: yOffset },
    data: {
      trigger_type: journey.trigger_type || 'checkout_abandoned',
      label: journey.trigger_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Trigger',
      exit_triggers: exitTriggers,
    },
  });
  
  // Convert legacy nodes
  let prevNodeId = triggerId;
  const legacyNodes = journey.nodes || [];
  
  legacyNodes.forEach((legacyNode, index) => {
    yOffset += ySpacing;
    const nodeId = legacyNode.id || `node_${index}_${Date.now()}`;
    
    let nodeType = 'action';
    let nodeData = { ...legacyNode };
    
    if (legacyNode.type === 'whatsapp') {
      nodeType = 'action';
      nodeData = {
        action_type: 'whatsapp',
        template_name: legacyNode.template_name,
        template_variables: legacyNode.template_variables || {},
        header_media_source: legacyNode.header_media_source,
        label: 'Send WhatsApp',
      };
    } else if (legacyNode.type === 'delay') {
      nodeType = 'delay';
      nodeData = {
        delay_value: legacyNode.delay_value || 1,
        delay_unit: legacyNode.delay_unit || 'hours',
        label: 'Wait',
      };
    } else if (legacyNode.type === 'condition') {
      nodeType = 'condition';
      nodeData = {
        condition_field: legacyNode.condition_field || 'order.total',
        condition_operator: legacyNode.condition_operator || '>',
        condition_value: legacyNode.condition_value || '0',
        label: 'If / Else',
      };
    }
    
    nodes.push({
      id: nodeId,
      type: nodeType,
      position: { x: xCenter, y: yOffset },
      data: nodeData,
    });
    
    // Add edge from previous node
    edges.push({
      id: `edge_${prevNodeId}_${nodeId}`,
      source: prevNodeId,
      target: nodeId,
    });
    
    prevNodeId = nodeId;
  });
  
  // Add end node
  if (legacyNodes.length > 0) {
    yOffset += ySpacing;
    const endId = `end_${Date.now()}`;
    nodes.push({
      id: endId,
      type: 'end',
      position: { x: xCenter, y: yOffset },
      data: { label: 'End Journey' },
    });
    
    edges.push({
      id: `edge_${prevNodeId}_${endId}`,
      source: prevNodeId,
      target: endId,
    });
  }
  
  return { nodes, edges };
};

// Convert React Flow format back to backend-compatible format
const flowToBackendFormat = (flowData, journeyMeta) => {
  const { nodes, edges } = flowData;
  
  // Find trigger node
  const triggerNode = nodes.find(n => n.type === 'trigger');
  const trigger_type = triggerNode?.data?.trigger_type || 'checkout_abandoned';
  
  // Merge exit conditions from trigger node and journey settings
  // Trigger node exit_triggers take precedence
  const triggerExitConditions = triggerNode?.data?.exit_triggers || [];
  const settingsExitConditions = journeyMeta.exit_triggers || [];
  
  // Combine both, but prefer trigger node's settings (they're more current)
  // Use the trigger node's exit conditions if any, otherwise use settings
  const mergedExitConditions = triggerExitConditions.length > 0 
    ? triggerExitConditions 
    : settingsExitConditions;
  
  // Build node sequence from edges
  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  const edgesBySource = new Map();
  edges.forEach(e => {
    if (!edgesBySource.has(e.source)) {
      edgesBySource.set(e.source, []);
    }
    edgesBySource.get(e.source).push(e);
  });
  
  // Convert nodes to backend format (excluding trigger and end)
  const backendNodes = nodes
    .filter(n => n.type !== 'trigger' && n.type !== 'end')
    .map(n => {
      const baseNode = {
        id: n.id,
        position: n.position,
      };
      
      if (n.type === 'action') {
        return {
          ...baseNode,
          type: n.data.action_type || 'whatsapp',
          template_name: n.data.template_name,
          template_variables: n.data.template_variables || {},
          header_media_source: n.data.header_media_source,
          header_media_url: n.data.header_media_url,
          button_url_mappings: n.data.button_url_mappings,
        };
      } else if (n.type === 'delay') {
        return {
          ...baseNode,
          type: 'delay',
          delay_value: n.data.delay_value || 1,
          delay_unit: n.data.delay_unit || 'hours',
        };
      } else if (n.type === 'condition') {
        return {
          ...baseNode,
          type: 'condition',
          condition_field: n.data.condition_field,
          condition_operator: n.data.condition_operator,
          condition_value: n.data.condition_value,
        };
      }
      
      return baseNode;
    });
  
  return {
    ...journeyMeta,
    trigger_type,
    exit_triggers: mergedExitConditions,  // Use merged exit conditions
    exit_conditions: mergedExitConditions, // Also set exit_conditions for compatibility
    nodes: backendNodes,
    flow_data: flowData, // Store the full flow data for the visual builder
  };
};

export default function JourneyBuilderPage() {
  const navigate = useNavigate();
  const { journeyId } = useParams();
  const location = useLocation();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [journey, setJourney] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showTemplateGallery, setShowTemplateGallery] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showExecutions, setShowExecutions] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [dateRange, setDateRange] = useState('30');
  
  // Journey metadata
  const [journeyName, setJourneyName] = useState('');
  const [journeyDescription, setJourneyDescription] = useState('');
  const [journeyStatus, setJourneyStatus] = useState('draft');
  const [exitConditions, setExitConditions] = useState([]);
  const [preventReentry, setPreventReentry] = useState(true);
  const [cooldownHours, setCooldownHours] = useState(0);

  // Live-edit prompt: when saving an already-active journey that has
  // in-flight executions, ask the user what should happen to those
  // existing customers (continue on the snapshot, or exit and let
  // only newly-triggered customers see the new flow).
  const [liveEditPrompt, setLiveEditPrompt] = useState({
    open: false,
    activeCount: 0,
    pendingFlowData: null,
  });
  
  // Flow data for the canvas
  const [initialNodes, setInitialNodes] = useState([]);
  const [initialEdges, setInitialEdges] = useState([]);

  // Check if we should show template gallery on new journey
  useEffect(() => {
    if (journeyId === 'new' && !location.state?.skipTemplates) {
      setShowTemplateGallery(true);
    }
  }, [journeyId, location.state]);

  // Load journey and templates
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        
        // Load templates
        try {
          const templatesRes = await axios.get(`${API_BASE_URL}/whatsapp/templates`, { headers });
          setTemplates(templatesRes.data.templates || []);
        } catch (e) {
          console.log('Could not load WhatsApp templates');
        }
        
        if (journeyId && journeyId !== 'new') {
          // Load existing journey
          const journeyRes = await axios.get(`${API_BASE_URL}/v1/journeys/${journeyId}`, { headers });
          const journeyData = journeyRes.data;
          setJourney(journeyData);
          setJourneyName(journeyData.name || '');
          setJourneyDescription(journeyData.description || '');
          setJourneyStatus(journeyData.status || 'draft');
          setExitConditions(journeyData.exit_conditions || journeyData.exit_triggers || []);
          setPreventReentry(journeyData.prevent_reentry !== false);
          setCooldownHours(journeyData.cooldown_hours || 0);
          
          // Convert to flow format
          const flowData = legacyToFlowFormat(journeyData);
          // Backward-compat: legacy edges from action nodes have no sourceHandle.
          // Stamp them to "on_send" so new multi-outcome handles render correctly.
          const actionNodeIds = new Set(
            (flowData.nodes || []).filter(n => n.type === 'action').map(n => n.id)
          );
          const normalizedEdges = (flowData.edges || []).map(e => {
            if (actionNodeIds.has(e.source) && (!e.sourceHandle || e.sourceHandle === 'default')) {
              return { ...e, sourceHandle: 'on_send' };
            }
            return e;
          });
          setInitialNodes(flowData.nodes);
          setInitialEdges(normalizedEdges);
          
          // Load analytics if journey is active
          if (journeyData.status === 'active') {
            loadAnalytics(journeyId);
          }
        } else {
          // New journey - start with default trigger
          const defaultNodes = [
            {
              id: `trigger_${Date.now()}`,
              type: 'trigger',
              position: { x: 300, y: 50 },
              data: { trigger_type: 'checkout_abandoned', label: 'Checkout Abandoned' },
            },
          ];
          setInitialNodes(defaultNodes);
          setInitialEdges([]);
          setJourneyName('New Journey');
        }
      } catch (error) {
        console.error('Error loading journey:', error);
        if (journeyId && journeyId !== 'new') {
          toast.error('Failed to load journey');
        }
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [journeyId]);

  // Load analytics
  const loadAnalytics = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/v1/journeys/${id}/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    }
  };

  // Reload analytics when date range changes
  useEffect(() => {
    if (showAnalytics && journey?.id) {
      loadAnalytics(journey.id);
    }
  }, [showAnalytics, dateRange]);

  // Handle template selection
  const handleTemplateSelected = (newJourney) => {
    navigate(`/marketing/journeys/builder/${newJourney.id}`, { replace: true });
  };

  // Persist the journey edit. `onExisting` is one of:
  //   undefined → no special handling (initial save, paused/draft journey,
  //               or a live journey with zero in-flight executions).
  //   'continue' → keep existing customers running on the old snapshot.
  //   'exit'     → terminate every in-flight execution.
  // Returns true on success, false on failure.
  const persistJourney = useCallback(async (flowData, onExisting) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const backendData = flowToBackendFormat(flowData, {
        name: journeyName,
        description: journeyDescription,
        status: journeyStatus,
        exit_triggers: exitConditions,
        prevent_reentry: preventReentry,
        cooldown_hours: cooldownHours,
      });

      if (journeyId && journeyId !== 'new') {
        const url = onExisting
          ? `${API_BASE_URL}/v1/journeys/${journeyId}?on_existing_executions=${onExisting}`
          : `${API_BASE_URL}/v1/journeys/${journeyId}`;
        const res = await axios.put(url, backendData, { headers });
        const exited = res?.data?.exited_executions || 0;
        if (onExisting === 'exit' && exited > 0) {
          toast.success(`Journey saved. ${exited} existing customer${exited === 1 ? '' : 's'} exited.`);
        } else if (onExisting === 'continue') {
          toast.success('Journey saved. Existing customers will continue.');
        } else {
          toast.success('Journey saved successfully');
        }
      } else {
        const response = await axios.post(`${API_BASE_URL}/v1/journeys`, backendData, { headers });
        toast.success('Journey created successfully');
        navigate(`/marketing/journeys/builder/${response.data.id}`, { replace: true });
      }
      return true;
    } catch (error) {
      console.error('Error saving journey:', error);
      toast.error(error.response?.data?.detail || 'Failed to save journey');
      return false;
    } finally {
      setSaving(false);
    }
  }, [journeyId, journeyName, journeyDescription, journeyStatus, exitConditions, preventReentry, cooldownHours, navigate]);

  // Save journey — wraps persistJourney with the live-edit prompt logic.
  const handleSave = useCallback(async (flowData) => {
    if (!journeyName.trim()) {
      toast.error('Please enter a journey name');
      setShowSettingsDialog(true);
      return;
    }

    // Live journey edit? Ask the operator what to do with in-flight
    // customers BEFORE writing. Only triggers when:
    //   * editing an existing journey (not a new one)
    //   * the journey is currently `active`
    //   * there is at least one in-flight execution
    const isLiveEdit = journeyId && journeyId !== 'new' && journeyStatus === 'active';
    if (isLiveEdit) {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(
          `${API_BASE_URL}/v1/journeys/${journeyId}/active-executions-count`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if ((data?.count || 0) > 0) {
          setLiveEditPrompt({
            open: true,
            activeCount: data.count,
            pendingFlowData: flowData,
          });
          return;
        }
      } catch (e) {
        // If the count call fails, fall through to a normal save —
        // surfacing a misleading dialog is worse than skipping it.
      }
    }

    await persistJourney(flowData);
  }, [journeyId, journeyName, journeyStatus, persistJourney]);

  // Toggle journey status
  const toggleStatus = async () => {
    const newStatus = journeyStatus === 'active' ? 'paused' : 'active';
    setJourneyStatus(newStatus);
    
    if (journeyId && journeyId !== 'new') {
      try {
        const token = localStorage.getItem('token');
        await axios.put(
          `${API_BASE_URL}/v1/journeys/${journeyId}/status`,
          { status: newStatus },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`Journey ${newStatus === 'active' ? 'activated' : 'paused'}`);
      } catch (error) {
        toast.error('Failed to update status');
        setJourneyStatus(journeyStatus); // Revert
      }
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-wt-bg">
        <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-wt-bg">
      {/* Header */}
      <div className="h-16 border-b border-wt-border bg-wt-surface flex items-center justify-between px-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/marketing/journeys')}
            className="text-wt-text-muted hover:text-wt-text-primary"
            data-testid="journey-builder-back-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          
          <div className="h-6 w-px bg-wt-border" />
          
          <div>
            <h1 className="text-lg font-semibold text-wt-text-primary">
              {journeyName || 'Untitled Journey'}
            </h1>
            <div className="flex items-center gap-2">
              <p className="text-xs text-wt-text-muted">
                Visual Journey Builder
              </p>
              {exitConditions.length > 0 && (
                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs">
                  <ShieldAlert className="h-3 w-3 mr-1" />
                  {exitConditions.length} exit condition{exitConditions.length > 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Analytics Toggle - Show for any saved journey */}
          {journeyId && journeyId !== 'new' && (
            <Button
              variant={showAnalytics ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowAnalytics(!showAnalytics)}
              className={showAnalytics ? 'bg-wt-gold text-black' : 'border-wt-border text-wt-text-muted'}
              data-testid="toggle-analytics-btn"
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              {showAnalytics ? 'Hide Analytics' : 'Analytics'}
            </Button>
          )}

          {/* Executions Toggle — Phase: Journey Logs Viewer (Feb 18, 2026) */}
          {journeyId && journeyId !== 'new' && (
            <Button
              variant={showExecutions ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowExecutions(!showExecutions)}
              className={showExecutions ? 'bg-wt-gold text-black' : 'border-wt-border text-wt-text-muted'}
              data-testid="toggle-executions-btn"
            >
              <Activity className="h-4 w-4 mr-2" />
              {showExecutions ? 'Hide Executions' : 'Executions'}
            </Button>
          )}
          
          {/* Status Toggle */}
          {journeyId && journeyId !== 'new' && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-wt-text-muted">
                {journeyStatus === 'active' ? 'Active' : 'Paused'}
              </span>
              <Switch
                checked={journeyStatus === 'active'}
                onCheckedChange={toggleStatus}
                data-testid="journey-status-toggle"
              />
            </div>
          )}
          
          {/* Settings */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSettingsDialog(true)}
            className="border-wt-border text-wt-text-muted"
            data-testid="journey-settings-btn"
          >
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
        </div>
      </div>
      
      {/* Main Content with Optional Analytics */}
      <div className="flex-1 flex overflow-hidden">
        {/* Analytics Panel (when shown) */}
        {showAnalytics && journey && (
          <AnalyticsPanel
            journey={journey}
            analytics={analytics}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            onClose={() => setShowAnalytics(false)}
            expanded={false}
          />
        )}
        
        {/* Canvas */}
        <div className="flex-1 overflow-hidden">
          <JourneyCanvas
            initialNodes={initialNodes}
            initialEdges={initialEdges}
            onSave={handleSave}
            journey={journey}
            templates={templates}
            isReadOnly={false}
            showAnalytics={showAnalytics}
            analytics={analytics}
          />
        </div>

        {/* Executions Panel (when shown) */}
        {showExecutions && journey && (
          <JourneyExecutionsPanel
            journey={journey}
            onClose={() => setShowExecutions(false)}
          />
        )}
      </div>
      
      {/* Template Gallery Modal */}
      <TemplateGallery
        open={showTemplateGallery}
        onClose={() => setShowTemplateGallery(false)}
        onSelectTemplate={handleTemplateSelected}
      />
      
      {/* Settings Dialog - Enhanced with Exit Conditions */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="bg-wt-surface border-wt-border max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="pb-4 border-b border-wt-border">
            <DialogTitle className="text-wt-text-primary">Journey Settings</DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="general" className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="bg-wt-paper border border-wt-border mx-4 mt-4">
              <TabsTrigger value="general" className="data-[state=active]:bg-wt-gold data-[state=active]:text-black">
                General
              </TabsTrigger>
              <TabsTrigger value="exit" className="data-[state=active]:bg-wt-gold data-[state=active]:text-black">
                Exit Conditions
              </TabsTrigger>
              <TabsTrigger value="advanced" className="data-[state=active]:bg-wt-gold data-[state=active]:text-black">
                Advanced
              </TabsTrigger>
            </TabsList>
            
            <div className="flex-1 overflow-y-auto px-4 pb-4">
              <TabsContent value="general" className="mt-4 space-y-4">
                <div>
                  <Label className="text-wt-text-primary">Journey Name</Label>
                  <Input
                    value={journeyName}
                    onChange={(e) => setJourneyName(e.target.value)}
                    placeholder="Enter journey name"
                    className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                    data-testid="journey-name-input"
                  />
                </div>
                
                <div>
                  <Label className="text-wt-text-primary">Description</Label>
                  <Input
                    value={journeyDescription}
                    onChange={(e) => setJourneyDescription(e.target.value)}
                    placeholder="Optional description"
                    className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                    data-testid="journey-description-input"
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="exit" className="mt-4">
                <ExitConditionsConfig
                  exitConditions={exitConditions}
                  onChange={setExitConditions}
                />
              </TabsContent>
              
              <TabsContent value="advanced" className="mt-4 space-y-4">
                {/* Re-entry Prevention */}
                <div className="p-4 bg-wt-paper rounded-lg border border-wt-border">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-wt-text-primary font-medium">Prevent Re-entry</Label>
                      <p className="text-xs text-wt-text-muted mt-1">
                        Stop the same customer from entering this journey multiple times simultaneously
                      </p>
                    </div>
                    <Switch
                      checked={preventReentry}
                      onCheckedChange={setPreventReentry}
                      data-testid="prevent-reentry-toggle"
                    />
                  </div>
                </div>
                
                {/* Cooldown Period */}
                <div className="p-4 bg-wt-paper rounded-lg border border-wt-border">
                  <Label className="text-wt-text-primary font-medium">Cooldown Period</Label>
                  <p className="text-xs text-wt-text-muted mt-1 mb-3">
                    Minimum time before a customer can re-enter this journey (0 = no cooldown)
                  </p>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      value={cooldownHours}
                      onChange={(e) => setCooldownHours(parseInt(e.target.value) || 0)}
                      className="bg-wt-bg border-wt-border text-wt-text-primary w-24"
                      data-testid="cooldown-hours-input"
                    />
                    <span className="text-sm text-wt-text-muted">hours</span>
                  </div>
                </div>
              </TabsContent>
            </div>
          </Tabs>
          
          <div className="p-4 border-t border-wt-border flex justify-end">
            <Button
              onClick={() => setShowSettingsDialog(false)}
              className="bg-wt-gold text-black hover:bg-wt-gold/90"
              data-testid="settings-done-btn"
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Live-edit prompt: how to handle in-flight executions */}
      <Dialog
        open={liveEditPrompt.open}
        onOpenChange={(open) => {
          if (!open) setLiveEditPrompt({ open: false, activeCount: 0, pendingFlowData: null });
        }}
      >
        <DialogContent className="bg-wt-surface border-wt-border max-w-md" data-testid="journey-live-edit-prompt">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-400" />
              Existing customers in this journey
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-wt-text-secondary">
            <p>
              <span className="font-semibold text-wt-text-primary">
                {liveEditPrompt.activeCount}
              </span>{' '}
              customer{liveEditPrompt.activeCount === 1 ? ' is' : 's are'} currently progressing through this journey.
              What should happen to them after you save your changes?
            </p>
            <ul className="text-xs text-wt-text-muted list-disc pl-4 space-y-1">
              <li><b className="text-wt-text-secondary">Continue</b> — they finish on the version they entered with. Only newly-triggered customers see the new flow.</li>
              <li><b className="text-wt-text-secondary">Exit</b> — terminate them now. They will not receive further messages from this journey.</li>
            </ul>
          </div>
          <div className="pt-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => setLiveEditPrompt({ open: false, activeCount: 0, pendingFlowData: null })}
              disabled={saving}
              data-testid="journey-live-edit-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              className="border-red-500/40 text-red-300 hover:bg-red-500/10"
              disabled={saving}
              onClick={async () => {
                const flowData = liveEditPrompt.pendingFlowData;
                setLiveEditPrompt({ open: false, activeCount: 0, pendingFlowData: null });
                if (flowData) await persistJourney(flowData, 'exit');
              }}
              data-testid="journey-live-edit-exit"
            >
              Exit existing
            </Button>
            <Button
              className="bg-wt-gold text-black hover:bg-wt-gold/90"
              disabled={saving}
              onClick={async () => {
                const flowData = liveEditPrompt.pendingFlowData;
                setLiveEditPrompt({ open: false, activeCount: 0, pendingFlowData: null });
                if (flowData) await persistJourney(flowData, 'continue');
              }}
              data-testid="journey-live-edit-continue"
            >
              Continue existing
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
